import { useCallback, useEffect, useState } from 'react';
import { useBotRoute } from '../lib/useBotRoute';
import {
  hubClient,
  SUGGESTED_TENANT_KEYS,
  type TenantConfig,
  type UserProfile,
} from '../services/hubClient';

const labelCls = 'block text-xs font-medium text-gray-500 mb-1';
const inputCls = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';
const monoCls = 'font-mono text-xs';

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Parse a tenant-config value: valid JSON if possible, else the raw string. */
function parseLooseValue(text: string): unknown {
  const t = text.trim();
  if (t === '') return '';
  try {
    return JSON.parse(t);
  } catch {
    return text;
  }
}

// ── Tenant config ─────────────────────────────────────────────────────────────

function TenantConfigPanel() {
  const { system, botId } = useBotRoute();
  const [tenantId, setTenantId] = useState('');
  const [resolvedNote, setResolvedNote] = useState<string | null>(null);
  const [rows, setRows] = useState<TenantConfig[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // edit form
  const [key, setKey] = useState('');
  const [valueText, setValueText] = useState('');
  const [description, setDescription] = useState('');

  // Try to resolve the tenant for the current bot on mount / bot change.
  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    (async () => {
      const tid = await hubClient.resolveTenantId(system, botId).catch(() => null);
      if (cancelled) return;
      if (tid) {
        setTenantId(tid);
        setResolvedNote(`Resolved from ${system}/${botId}`);
      } else {
        setResolvedNote(`No tenant mapped for ${system}/${botId} — enter a tenantId manually.`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [system, botId]);

  const load = useCallback(async () => {
    if (!tenantId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await hubClient.listTenantConfig(tenantId.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tenant config');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  function editRow(row: TenantConfig) {
    setKey(row.key);
    setValueText(pretty(row.value));
    setDescription(row.description ?? '');
  }

  async function save() {
    if (!tenantId.trim() || !key.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await hubClient.putTenantConfig(tenantId.trim(), key.trim(), {
        value: parseLooseValue(valueText),
        description: description.trim() || undefined,
      });
      setKey('');
      setValueText('');
      setDescription('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function remove(rowKey: string) {
    if (!confirm(`Delete tenant-config key "${rowKey}"?`)) return;
    setError(null);
    try {
      await hubClient.deleteTenantConfig(tenantId.trim(), rowKey);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <label className={labelCls}>Tenant ID</label>
        <div className="flex gap-2">
          <input
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className={`${inputCls} ${monoCls}`}
            placeholder="tenant uuid"
          />
          <button onClick={load} disabled={!tenantId.trim() || loading} className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-40">
            {loading ? 'Loading…' : 'Load'}
          </button>
        </div>
        {resolvedNote && <p className="mt-2 text-xs text-gray-400">{resolvedNote}</p>}
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {rows && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold">Keys ({rows.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {rows.map((r) => (
              <div key={r.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className={`${monoCls} font-semibold text-gray-900`}>{r.key}</div>
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">{pretty(r.value)}</pre>
                  {r.description && <p className="mt-1 text-xs text-gray-400">{r.description}</p>}
                </div>
                <button onClick={() => editRow(r)} className="rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50">Edit</button>
                <button onClick={() => remove(r.key)} className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-red-50 hover:text-red-600">Delete</button>
              </div>
            ))}
            {rows.length === 0 && <p className="px-4 py-6 text-sm text-gray-400">No keys for this tenant yet.</p>}
          </div>
        </div>
      )}

      {tenantId.trim() && rows && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold">{key ? `Edit "${key}"` : 'Add key'}</h3>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Key</label>
              <input value={key} onChange={(e) => setKey(e.target.value)} className={`${inputCls} ${monoCls}`} placeholder="fallback_behavior" />
              <div className="mt-2 flex flex-wrap gap-1">
                {SUGGESTED_TENANT_KEYS.map((k) => (
                  <button key={k} onClick={() => setKey(k)} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200">{k}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Value (JSON, or plain text for a string)</label>
              <textarea value={valueText} onChange={(e) => setValueText(e.target.value)} className={`${inputCls} ${monoCls}`} rows={4} placeholder='"escalate_human"  or  ["a","b"]  or  {"k":1}' />
            </div>
            <div>
              <label className={labelCls}>Description (optional)</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
            </div>
            <div className="flex justify-end gap-2">
              {key && (
                <button onClick={() => { setKey(''); setValueText(''); setDescription(''); }} className="rounded px-3 py-1.5 text-sm text-gray-600">Clear</button>
              )}
              <button onClick={save} disabled={saving || !key.trim()} className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40">
                {saving ? 'Saving…' : 'Save key'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── User profile ──────────────────────────────────────────────────────────────

function UserProfilePanel() {
  const { system } = useBotRoute();
  const [psid, setPsid] = useState('');
  const [pageId, setPageId] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [attrsText, setAttrsText] = useState('{}');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!psid.trim()) return;
    setLoading(true);
    setError(null);
    setLoaded(false);
    try {
      const p = await hubClient.getUserProfile(system, psid.trim());
      setProfile(p);
      setAttrsText(pretty(p?.attrs ?? {}));
      setSummary(p?.summary ?? '');
      setPageId(p?.pageId ?? '');
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    let attrs: Record<string, unknown>;
    try {
      const parsed = attrsText.trim() === '' ? {} : JSON.parse(attrsText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('attrs must be a JSON object');
      }
      attrs = parsed as Record<string, unknown>;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'attrs is not valid JSON');
      setSaving(false);
      return;
    }
    try {
      const next = profile?.id
        ? await hubClient.updateUserProfile(profile.id, { attrs, summary: summary.trim() || undefined })
        : await hubClient.createUserProfile({
            system,
            psid: psid.trim(),
            pageId: pageId.trim() || undefined,
            attrs,
            summary: summary.trim() || undefined,
          });
      setProfile(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!profile?.id) return;
    if (!confirm('Delete this user profile?')) return;
    setError(null);
    try {
      await hubClient.deleteUserProfile(profile.id);
      setProfile(null);
      setAttrsText('{}');
      setSummary('');
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete profile');
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>System</label>
            <input value={system} disabled className={`${inputCls} bg-gray-50`} />
          </div>
          <div>
            <label className={labelCls}>PSID (channel user id)</label>
            <input value={psid} onChange={(e) => setPsid(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className={`${inputCls} ${monoCls}`} placeholder="messenger psid…" />
          </div>
          <div className="flex items-end">
            <button onClick={load} disabled={!psid.trim() || loading} className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-40">
              {loading ? 'Loading…' : 'Load'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loaded && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">{profile ? 'Profile' : 'No profile — create one'}</h3>
            {profile?.lastSeen && (
              <span className="text-xs text-gray-400">last seen {new Date(profile.lastSeen).toLocaleString()}</span>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Attributes (JSON object)</label>
              <textarea value={attrsText} onChange={(e) => setAttrsText(e.target.value)} className={`${inputCls} ${monoCls}`} rows={6} />
            </div>
            <div>
              <label className={labelCls}>Summary (assistant-written context)</label>
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} className={inputCls} rows={3} />
            </div>
            <div className="flex justify-between">
              {profile?.id ? (
                <button onClick={remove} className="rounded px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete profile</button>
              ) : (
                <span />
              )}
              <button onClick={save} disabled={saving} className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40">
                {saving ? 'Saving…' : profile?.id ? 'Save changes' : 'Create profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type Tab = 'tenant' | 'user';

export function MemoryConfig() {
  const [tab, setTab] = useState<Tab>('tenant');

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Memory</h1>
        <p className="mb-6 text-gray-600">
          Tenant-wide config (tone, fallbacks, disclaimers) and per-user long-term profiles. These
          are JWT-only routes — reached here with your SSO session.
        </p>

        <div className="mb-6 flex gap-2 border-b border-gray-200">
          {([
            ['tenant', 'Tenant config'],
            ['user', 'User profile'],
          ] as [Tab, string][]).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`-mb-px rounded-t-lg px-4 py-2 text-sm font-medium ${
                tab === id
                  ? '-mb-px border border-b-0 border-gray-200 bg-white text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'tenant' ? <TenantConfigPanel /> : <UserProfilePanel />}
      </div>
    </div>
  );
}

export default MemoryConfig;
